// Copyright 2023 Sorama B.V.

import { BindingEngine, Disposable, bindingMode, computedFrom, observable } from "aurelia-framework";
import { bindable, customElement } from "aurelia-templating";

export interface Filter<T> {
    name: string;
    filter: (item: T) => boolean;
    enabled?: boolean;
}

export interface Sorter<T> {
    name: string;
    icon?: string;
    comparator: (item1: T, item2: T) => number;
}

export interface DateFilter<T> {
    getDate: (item: T) => Date;
}

interface SorterOneWay<T> {
    name: string;
    icon?: string;
    comparator: (item1: T, item2: T) => number;
}

export type FilterStrategy = "and" | "or";

@customElement("filter-menu")
export class FilterMenu {
    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: "recalculateFilteredList" })
    public list: unknown[] = [];

    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: "recalculateFilteredList" })
    public searchKey = "name";

    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: "recalculateFilteredList" })
    public filters: Filter<unknown>[] = [];

    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: "recalculateFilteredList" })
    public filterStrategy: FilterStrategy = "or";

    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: "recalculateFilteredList" })
    public sorters: Sorter<unknown>[] = [];

    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: "recalculateFilteredList" })
    public dateFilter: DateFilter<unknown>;

    @bindable({ defaultBindingMode: bindingMode.fromView })
    public filteredList: unknown[] = [];

    @observable({ changeHandler: "recalculateFilteredList" })
    private searchString = "";

    private subscriptions: Disposable[] = [];

    @observable({ changeHandler: "recalculateFilteredList" })
    private selectedDateRange: { from: Date; to: Date };

    @computedFrom("filters", "list")
    private get applicableFilters(): Filter<unknown>[] {
        if (this.list.length === 1) return [];

        // only show filters that actually have an effect (at least one item is filtered out, and not all items pass through the filter)
        return this.filters.filter((f) => {
            const filteredList = this.list.filter((item) => f.filter(item));
            return filteredList.length !== this.list.length && filteredList.length !== 0;
        });
    }

    @observable({ changeHandler: "recalculateFilteredList" })
    private selectedSorter: SorterOneWay<unknown>;

    @computedFrom("sorters")
    private get sortersBothWays(): SorterOneWay<unknown>[] {
        const bothWaySorters = [];
        this.sorters.forEach((sorter) => {
            bothWaySorters.push({
                fullName: sorter.name + " Ascending",
                name: sorter.name,
                icon: sorter.icon ? sorter.icon + "-down" : undefined,
                comparator: sorter.comparator,
            });
            bothWaySorters.push({
                fullName: sorter.name + " Descending",
                name: sorter.name,
                icon: sorter.icon ? sorter.icon + "-up" : undefined,
                comparator: (item1, item2) => sorter.comparator(item2, item1),
            });
        });

        if (!this.selectedSorter && bothWaySorters.length > 0) {
            this.selectedSorter = bothWaySorters[0];
        }

        return bothWaySorters;
    }

    constructor(private readonly bindingEngine: BindingEngine) {}

    attached(): void {
        this.subscriptions = [
            this.bindingEngine.propertyObserver(this, "list").subscribe(() => this.reset()),
            this.bindingEngine.collectionObserver(this.filters).subscribe(() => this.recalculateFilteredList()),
            this.bindingEngine.collectionObserver(this.sorters).subscribe(() => this.recalculateFilteredList()),
            ...this.filters.map((filter) =>
                this.bindingEngine.propertyObserver(filter, "enabled").subscribe(() => this.recalculateFilteredList())
            ),
            ...this.sorters.map((filter) =>
                this.bindingEngine
                    .propertyObserver(filter, "sortingOption")
                    .subscribe(() => this.recalculateFilteredList())
            ),
        ];
    }

    detached(): void {
        this.subscriptions.forEach((subscription) => subscription.dispose());
    }

    private reset(): void {
        this.searchString = "";
        this.filters.forEach((filter) => (filter.enabled = false));
    }

    private recalculateFilteredList(): void {
        let filteredList = this.list;

        // Search string
        if (this.searchKey) {
            filteredList = filteredList.filter((item) =>
                item[this.searchKey]?.toLowerCase().includes(this.searchString.toLowerCase())
            );
        }

        // Filters
        filteredList = filteredList.filter((item) => this.isSatisfiedByFilters(item));

        // Filter by date
        if (this.dateFilter && this.selectedDateRange) {
            filteredList = filteredList.filter((item) => {
                if (this.dateFilter.getDate(item) == null) return true;
                return (
                    this.dateFilter.getDate(item).getTime() >= this.selectedDateRange.from.getTime() &&
                    this.dateFilter.getDate(item).getTime() <= this.selectedDateRange.to.getTime()
                );
            });
        }

        // Sort
        if (this.selectedSorter) {
            filteredList = filteredList.sort(this.selectedSorter.comparator);
        }
        this.filteredList = filteredList;
    }

    private isSatisfiedByFilters(item: unknown): boolean {
        // return true when no filters are enabled or no filters are defined
        if (this.applicableFilters.every((filter) => !filter.enabled)) return true;

        if (this.filterStrategy == "or") {
            return this.applicableFilters.some((filter) => filter.enabled && filter.filter(item));
        } else {
            return this.applicableFilters.every((filter) => !filter.enabled || filter.filter(item));
        }
    }
}
